// src/pages/Admin/Dashboard/VisitsChart.jsx
import React, { useMemo, useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "./../../../services/visitesServices";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const nf = new Intl.NumberFormat("fr-FR");

const toDateOnly = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const VisitsChart = () => {
  const [data, setData] = useState([]);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [loading, setLoading] = useState(false);

  // Filtre d’affichage (client-side)
  const [range, setRange] = useState("30"); // all | 7 | 30 | 90

  const fetchVisites = async () => {
    try {
      const response = await countVisitesByDate();
      setData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des visites :", error);
    }
  };

  useEffect(() => {
    fetchVisites();
  }, []);

  const filteredData = useMemo(() => {
    if (range === "all") return data;

    const days = Number(range);
    const now = toDateOnly(new Date());
    const start = new Date(now);
    start.setDate(start.getDate() - (days - 1));

    return data.filter((d) => {
      const dt = toDateOnly(d.date_visite);
      return dt >= start && dt <= now;
    });
  }, [data, range]);

  const totals = useMemo(() => {
    const total = filteredData.reduce((acc, d) => acc + Number(d.compteur || 0), 0);

    // moyenne/jour sur la fenêtre affichée (si vide -> 0)
    const days = range === "all"
      ? Math.max(1, filteredData.length)
      : Number(range);

    const avg = total / Math.max(1, days);

    return { total, avg };
  }, [filteredData, range]);

  const handleDelete = async () => {
    if (!dateDebut || !dateFin) return;

    const ok = window.confirm(
      `Supprimer les visites du ${dateDebut} au ${dateFin} ?`
    );
    if (!ok) return;

    setLoading(true);
    try {
      await deleteVisitesByDate(dateDebut, dateFin);
      await fetchVisites();
      setDateDebut("");
      setDateFin("");
    } catch (error) {
      console.error("Erreur lors de la suppression des visites :", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header + KPIs */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            Évolution des visites
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Filtre d’affichage + indicateurs rapides.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="inline-flex rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30 p-1">
            {[
              { k: "7", label: "7j" },
              { k: "30", label: "30j" },
              { k: "90", label: "90j" },
              { k: "all", label: "Tout" },
            ].map((b) => (
              <button
                key={b.k}
                onClick={() => setRange(b.k)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-semibold transition",
                  range === b.k
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900/40"
                )}
              >
                {b.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchVisites}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition"
          >
            Recharger
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total (période affichée)
          </div>
          <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
            {nf.format(totals.total)}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Moyenne / jour
          </div>
          <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
            {nf.format(Math.round(totals.avg))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30 p-4">
        <div style={{ position: "relative", zIndex: 0 }}>
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.4)" />
              <XAxis dataKey="date_visite" stroke="rgba(100,116,139,1)" />
              <YAxis stroke="rgba(100,116,139,1)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(15,23,42,0.95)",
                  border: "1px solid rgba(148,163,184,0.2)",
                  color: "#fff",
                  borderRadius: 12,
                }}
                cursor={{ stroke: "rgba(148,163,184,0.6)", strokeDasharray: "3 3" }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="compteur"
                name="Visites"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Danger zone */}
      <details className="mt-6 rounded-2xl border border-rose-200/70 dark:border-rose-900/40 bg-rose-50/60 dark:bg-rose-950/20 p-4">
        <summary className="cursor-pointer select-none list-none">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-rose-800 dark:text-rose-200">
                Zone sensible : supprimer des visites
              </div>
              <div className="text-sm text-rose-700/80 dark:text-rose-200/70 mt-1">
                Utile pour garder une base légère (ex : anciennes périodes).
              </div>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-rose-500/10 text-rose-800 dark:text-rose-200">
              Attention
            </span>
          </div>
        </summary>

        <div className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="date"
              className="p-2 rounded-xl border border-rose-200/70 dark:border-rose-900/40 bg-white/70 dark:bg-slate-950/40 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-rose-500/30"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
            <input
              type="date"
              className="p-2 rounded-xl border border-rose-200/70 dark:border-rose-900/40 bg-white/70 dark:bg-slate-950/40 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-rose-500/30"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
            <button
              className={cn(
                "p-2 rounded-xl text-white font-semibold transition",
                loading ? "bg-slate-500" : "bg-rose-600 hover:bg-rose-700"
              )}
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Suppression..." : "Supprimer"}
            </button>
          </div>
          <p className="text-xs text-rose-700/80 dark:text-rose-200/70 mt-3">
            Astuce : supprime par tranches (ex : année par année) si tu as énormément de données.
          </p>
        </div>
      </details>
    </div>
  );
};

export default VisitsChart;
